import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Typography,
  Card,
  Alert
} from '@mui/joy';
import { useAuth } from '../context/AuthContext';
import BrandMark from '../components/BrandMark';

const ForgotPassword = () => {
  const [searchParams] = useSearchParams();
  const emailFromUrl = searchParams.get('email') || '';
  const [step, setStep] = useState(emailFromUrl ? 'reset' : 'request');
  const [email, setEmail] = useState(emailFromUrl);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { requestPasswordReset, resetPassword } = useAuth();

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    const result = await requestPasswordReset(email);

    if (result.success) {
      setSuccess(result.message);
      setStep('reset');
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    const result = await resetPassword(email, code, newPassword);

    if (result.success) {
      setSuccess('Password reset successfully! You can now log in.');
      setCode('');
      setNewPassword('');
      setConfirmPassword('');
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.body',
        padding: 2
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 400 }}>
        <BrandMark subtitle="Password Reset" />

        {error && (
          <Alert color="danger" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert color="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {step === 'request' ? (
          <form onSubmit={handleRequestCode}>
            <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
              Enter your email and we'll send you a 6-digit code to reset your password.
            </Typography>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email"
              />
            </FormControl>
            <Button type="submit" loading={loading} fullWidth color="primary" sx={{ mb: 2 }}>
              Send Reset Code
            </Button>
          </form>
        ) : (
          <form onSubmit={handleResetPassword}>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email"
              />
            </FormControl>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>6-Digit Code</FormLabel>
              <Input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                placeholder="Enter code from email"
                inputProps={{ maxLength: 6 }}
              />
            </FormControl>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>New Password</FormLabel>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                placeholder="Enter new password"
              />
            </FormControl>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Confirm Password</FormLabel>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Confirm new password"
              />
            </FormControl>
            <Button type="submit" loading={loading} fullWidth color="primary" sx={{ mb: 2 }}>
              Reset Password
            </Button>
            <Button
              variant="outlined"
              color="neutral"
              fullWidth
              sx={{ mb: 2 }}
              onClick={() => {
                setStep('request');
                setCode('');
                setNewPassword('');
                setConfirmPassword('');
                setError('');
                setSuccess('');
              }}
            >
              Use Different Email
            </Button>
          </form>
        )}

        <Typography level="body-sm" sx={{ textAlign: 'center' }}>
          Back to{' '}
          <Link to="/login" style={{ color: 'var(--joy-palette-primary-500)' }}>
            Login
          </Link>
        </Typography>
      </Card>
    </Box>
  );
};

export default ForgotPassword;
